import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaBell } from "react-icons/fa";
import { API_URL } from "../../../config";
import { getSocket, initSocket } from "../../../socket";

const UniversalNotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const hasPlayedInitialReminderRef = useRef(false);
  const pendingReminderSoundRef = useRef(false);

  const token = localStorage.getItem("token");
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();
  const isEmployeeRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/employee");
  const isEmployee =
    isEmployeeRoute ||
    String(user?.role || "").toLowerCase() === "employee" ||
    Boolean(user?.employeeId);
  const employeeId = user?.employeeId || user?._id;

  const isNoticeBoardNotification = (n) => {
    const moduleName = String(n?.module || "").toLowerCase();
    const typeName = String(n?.type || "").toLowerCase();
    const sourceName = String(n?.source || "").toLowerCase();
    const categoryName = String(n?.category || "").toLowerCase();
    return (
      moduleName.includes("notice") ||
      typeName.includes("notice") ||
      sourceName.includes("notice") ||
      categoryName.includes("notice")
    );
  };

  const playRenewalReminderSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        pendingReminderSoundRef.current = true;
        ctx.close().catch(() => { });
        return;
      }
      const totalDurationSec = 5;
      const beepDurationSec = 0.35;
      const gapDurationSec = 0.15;
      const cycles = Math.floor(totalDurationSec / (beepDurationSec + gapDurationSec));

      for (let i = 0; i < cycles; i += 1) {
        const startAt = ctx.currentTime + i * (beepDurationSec + gapDurationSec);
        const stopAt = startAt + beepDurationSec;
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = "square";
        oscillator.frequency.setValueAtTime(880, startAt);
        oscillator.frequency.setValueAtTime(700, startAt + beepDurationSec / 2);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.35, startAt + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, stopAt);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start(startAt);
        oscillator.stop(stopAt);
      }

      setTimeout(() => {
        ctx.close().catch(() => { });
      }, (totalDurationSec + 0.3) * 1000);
    } catch {
      // Some browsers block autoplay audio until user interaction.
      pendingReminderSoundRef.current = true;
    }
  };

  const playTaskDeadlineSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        pendingReminderSoundRef.current = true;
        ctx.close().catch(() => { });
        return;
      }

      // Melodic dual-chime E5 -> G5 (659.25Hz -> 783.99Hz)
      const notes = [
        { freq: 659.25, startDelay: 0, duration: 0.4 },
        { freq: 783.99, startDelay: 0.15, duration: 0.6 }
      ];

      notes.forEach((note) => {
        const startAt = ctx.currentTime + note.startDelay;
        const stopAt = startAt + note.duration;
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(note.freq, startAt);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.linearRampToValueAtTime(0.25, startAt + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, stopAt);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.start(startAt);
        oscillator.stop(note.startDelay + note.duration);
      });

      setTimeout(() => {
        ctx.close().catch(() => { });
      }, 1500);
    } catch {
      pendingReminderSoundRef.current = true;
    }
  };

  const playSoundForNotification = (n) => {
    const type = String(n?.type || "").toUpperCase();
    const title = String(n?.title || "").toLowerCase();
    
    const isTask = (type === "TASK_DEADLINE_REMINDER") || title.includes("deadline reminder");
    if (isTask) {
      playTaskDeadlineSound();
    } else {
      playRenewalReminderSound();
    }
  };

  const isRenewalOrDeadlineReminder = (n) => {
    const type = String(n?.type || "").toUpperCase();
    const title = String(n?.title || "").toLowerCase();
    return (
      ["SERVICE_RENEWAL_REMINDER", "SERVICE_RENEWAL_REMINDER_TEST", "TASK_DEADLINE_REMINDER"].includes(type) ||
      title.includes("deadline reminder")
    );
  };

  const fetchNotifications = async () => {
    try {
      if (isEmployee) {
        const res = await axios.get(`${API_URL}/api/notifications/employee/${employeeId}`);
        const all = res.data?.notifications || [];
        const firstUnread = all.find(
          (n) => !n?.isRead && isRenewalOrDeadlineReminder(n)
        );
        if (firstUnread && !hasPlayedInitialReminderRef.current) {
          hasPlayedInitialReminderRef.current = true;
          playSoundForNotification(firstUnread);
        }
        const isTaskAssignment = (n) =>
          String(n?.title || "").trim().toLowerCase() === "new task assigned" ||
          String(n?.body || "").toLowerCase().includes("you have been assigned a new task");
        setNotifications(all.filter((n) => !isTaskAssignment(n)));
      } else {
        const res = await axios.get(`${API_URL}/api/notifications-all/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const all = res.data || [];
        const firstUnread = all.find(
          (n) => !n?.isRead && isRenewalOrDeadlineReminder(n)
        );
        if (firstUnread && !hasPlayedInitialReminderRef.current) {
          hasPlayedInitialReminderRef.current = true;
          playSoundForNotification(firstUnread);
        }
        setNotifications(all.filter((n) => !isNoticeBoardNotification(n)));
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  useEffect(() => {
    const unlockPendingReminderSound = () => {
      if (!pendingReminderSoundRef.current) return;
      pendingReminderSoundRef.current = false;
      playRenewalReminderSound();
    };
    window.addEventListener("click", unlockPendingReminderSound, { passive: true });
    window.addEventListener("keydown", unlockPendingReminderSound);
    window.addEventListener("touchstart", unlockPendingReminderSound, { passive: true });

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);

    const socket = getSocket() || (token ? initSocket(token) : null);
    const onNewNotification = (data) => {
      if (isRenewalOrDeadlineReminder(data)) {
        playSoundForNotification(data);
      }
      if ((isEmployee && isNoticeBoardNotification(data)) || (!isEmployee && !isNoticeBoardNotification(data))) {
        setNotifications((prev) => [data, ...prev]);
      }
    };

    if (socket) {
      socket.on("new-notification", onNewNotification);
    }

    return () => {
      clearInterval(interval);
      if (socket) socket.off("new-notification", onNewNotification);
      window.removeEventListener("click", unlockPendingReminderSound);
      window.removeEventListener("keydown", unlockPendingReminderSound);
      window.removeEventListener("touchstart", unlockPendingReminderSound);
    };
  }, [token, isEmployee, employeeId]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAsRead = async (id) => {
    try {
      if (isEmployee) {
        await axios.put(`${API_URL}/api/notifications/read/${id}`);
      } else {
        await axios.put(
          `${API_URL}/api/notifications-all/read/${id}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (isEmployee && employeeId) {
        await axios.put(`${API_URL}/api/notifications/employee/readAll/${employeeId}`);
      } else {
        await axios.put(
          `${API_URL}/api/notifications-all/read-all`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn btn-light position-relative"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge bg-danger rounded-pill">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen ? (
        <ul
          className="dropdown-menu dropdown-menu-end shadow-lg show"
          style={{
            width: "350px",
            maxHeight: "450px",
            overflowY: "auto",
            display: "block",
            position: "absolute",
            right: 0,
            top: "110%",
          }}
        >
          <li className="dropdown-header d-flex justify-content-between align-items-center p-3">
            <h6 className="m-0">Notifications</h6>
            {unreadCount > 0 && (
              <button
                className="btn btn-sm btn-link text-primary p-0"
                onClick={markAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </li>
          <li><hr className="dropdown-divider" /></li>
          {notifications.length === 0 ? (
            <li className="dropdown-item text-center py-3 text-muted">
              No notifications
            </li>
          ) : (
            notifications.map((n) => (
              <li
                key={n._id}
                className={`dropdown-item p-3 ${!n.isRead ? "bg-light fw-bold" : ""}`}
                style={{ whiteSpace: "normal", cursor: "pointer", borderBottom: "1px solid #f1f1f1" }}
                onClick={() => markAsRead(n._id)}
              >
                <div className="d-flex justify-content-between">
                  <span className="text-primary small">{n.title}</span>
                  <small className="text-muted">
                    {new Date(n.createdAt).toLocaleDateString()}
                  </small>
                </div>
                <div className="mt-1 small text-dark">{n.message || n.body || ""}</div>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default UniversalNotificationBell;
